// src/components/admin/AdminPanel.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { requireSupabase } from "@/lib/supabase";
import { fetchOrgMemberCapabilityMap } from "@/lib/capabilities/memberCapabilitiesClient";
import type { CapabilityOverrides } from "@/lib/capabilities/capabilityOverrides";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceOrg } from "@/contexts/WorkspaceOrgContext";
import { AdminPanelContent } from "./AdminPanelContent";
import { useAdminPanelAccountActions } from "./useAdminPanelAccountActions";
import { useAdminPanelMemberActions } from "./useAdminPanelMemberActions";
import { useAdminPanelOrgOptions } from "./useAdminPanelOrgOptions";
import { formatAdminError } from "./adminErrors";
import {
  formatMemberName,
  type MemberWithProfile,
  type MembershipRow,
  type OrgRole,
  type PlatformRole,
  type ProfileRow,
} from "./adminTypes";

// ── Component ─────────────────────────────────────────────────────────────────
export function AdminPanel(props: { embedded?: boolean; onExit?: () => void } = {}) {
  const auth = useAuth();
  const sb = useMemo(() => requireSupabase(), []);

  const activeOrgId = auth.activeOrganizationId;
  const { workspaceOrganizationId, setWorkspaceOrganizationId } = useWorkspaceOrg();
  const effectiveOrgId = workspaceOrganizationId;
  const currentUserId = auth.session?.user.id;
  const [activeOrgName, setActiveOrgName] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const [open, setOpen] = useState<Set<string>>(() => new Set(["pending"]));
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const [pendingProfiles, setPendingProfiles] = useState<ProfileRow[]>([]);
  const [approvedProfiles, setApprovedProfiles] = useState<ProfileRow[]>([]);
  const [memberships, setMemberships] = useState<MembershipRow[]>([]);
  const [activeMembers, setActiveMembers] = useState<MemberWithProfile[]>([]);
  const [orgOptions, setOrgOptions] = useState<{ id: string; name: string }[]>([]);

  const [roleByUser, setRoleByUser] = useState<Record<string, OrgRole>>({});
  const [blockReasonByUser, setBlockReasonByUser] = useState<Record<string, string>>({});
  const [addRoleByUser, setAddRoleByUser] = useState<Record<string, OrgRole>>({});
  const [platformRoleByUser, setPlatformRoleByUser] = useState<Record<string, string>>({});
  const [memberPlatformRoleEdits, setMemberPlatformRoleEdits] = useState<Record<string, PlatformRole>>({});
  const [memberLocations, setMemberLocations] = useState<Record<string, string[]>>({});
  const [addLocByUser, setAddLocByUser] = useState<Record<string, string>>({});
  const [memberPlants, setMemberPlants] = useState<Record<string, string[]>>({});
  const [addPlantByUser, setAddPlantByUser] = useState<Record<string, string>>({});
  const [orgPlantOptions, setOrgPlantOptions] = useState<
    Array<{ id: string; plant_code: string; name: string }>
  >([]);
  const [collapsedMembers, setCollapsedMembers] = useState<Set<string>>(new Set());
  const [memberCapabilityByUser, setMemberCapabilityByUser] = useState<
    Record<string, CapabilityOverrides>
  >({});
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgRole>("sales");
  const [invitePlatformRole, setInvitePlatformRole] = useState<PlatformRole>("");
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [mergeSourceUserId, setMergeSourceUserId] = useState("");
  const [mergeTargetUserId, setMergeTargetUserId] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [showMergeUsers, setShowMergeUsers] = useState(false);
  const [activeModal, setActiveModal] = useState<"users" | "contacts" | "org" | null>(null);
  const canPermanentlyDeleteUsers = auth.hasRole("platform_admin");
  const canAssignManufacturingOrgRole = auth.realHasRole("platform_owner", "platform_admin");
  const canRunAdminRpcs = auth.realHasRole("platform_admin") || auth.hasRole("owner", "admin", "org_owner", "org_admin");

  const membershipKeySet = useMemo(() => {
    const s = new Set<string>();
    for (const m of memberships) {
      if (!m.is_active) continue;
      s.add(`${m.user_id}:${m.organization_id}`);
    }
    return s;
  }, [memberships]);

  const rolesByUserId = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const row of memberships) {
      if (!row.is_active) continue;
      const existing = m.get(row.user_id) ?? [];
      existing.push(row.role);
      m.set(row.user_id, existing);
    }
    return m;
  }, [memberships]);

  const approvedRolePending = useMemo(() => {
    if (!effectiveOrgId) return [];
    return approvedProfiles.filter((p) => !membershipKeySet.has(`${p.id}:${effectiveOrgId}`));
  }, [approvedProfiles, membershipKeySet, effectiveOrgId]);

  const mergeUserOptions = useMemo(
    () =>
      activeMembers
        .map((m) => ({
          id: m.user_id,
          label: `${formatMemberName(m)}${m.email ? ` (${m.email})` : ""}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activeMembers]
  );

  // ── Refresh ────────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!effectiveOrgId) {
      setMsg("No active organization selected.");
      return;
    }
    const canTargetOrg =
      (auth.memberOrganizationIds ?? []).includes(effectiveOrgId) ||
      auth.realHasRole("platform_admin");
    if (!canTargetOrg) {
      setMsg("Not authorized for the selected workspace organization.");
      return;
    }
    if (!canRunAdminRpcs) {
      setPendingProfiles([]);
      setApprovedProfiles([]);
      setMemberships([]);
      setActiveMembers([]);
      setMemberCapabilityByUser({});
      return;
    }
    setMsg("");
    setLoading(true);
    try {
      const [org, pending, approved, orgMembersRpc, membersWithProfilesRpc, memberLocsRpc, platformRolesRpc, memberPlantsRpc, orgPlantsRpc] = await Promise.all([
        sb.from("organizations").select("id, name").eq("id", effectiveOrgId).maybeSingle(),
        sb.rpc("admin_list_org_profiles", { p_org_id: effectiveOrgId, p_access_status: "pending" }),
        sb.rpc("admin_list_org_profiles", { p_org_id: effectiveOrgId, p_access_status: "approved" }),
        sb.rpc("admin_list_org_members", { p_org_id: effectiveOrgId }),
        sb.rpc("admin_list_members_with_profiles", { p_org_id: effectiveOrgId }),
        sb.rpc("admin_list_member_locations", { p_org_id: effectiveOrgId }),
        sb.rpc("platform_get_member_platform_roles", { p_org_id: effectiveOrgId }),
        sb.rpc("admin_list_member_plants", { p_org_id: effectiveOrgId }),
        sb.rpc("admin_list_org_plants", { p_org_id: effectiveOrgId }),
      ]);

      if (org.error) throw org.error;
      if (pending.error) throw pending.error;
      if (approved.error) throw approved.error;
      if (orgMembersRpc.error) throw orgMembersRpc.error;

      setActiveOrgName((org.data as any)?.name ?? null);
      setPendingProfiles((pending.data as ProfileRow[]) ?? []);
      setApprovedProfiles((approved.data as ProfileRow[]) ?? []);
      setMemberships(((orgMembersRpc.data as any[]) ?? []) as MembershipRow[]);

      if (!membersWithProfilesRpc.error) {
        const raw = (membersWithProfilesRpc.data as any[]) ?? [];
        const mapped = raw.map((r: any) => ({
          user_id: r.user_id,
          organization_id: r.organization_id,
          roles: Array.isArray(r.roles) ? r.roles : r.role ? [r.role] : [],
          email: r.email,
          full_name: r.full_name,
          display_name: r.display_name,
          last_sign_in_at: r.last_sign_in_at,
        }));
        setActiveMembers(mapped);
        // Start all member cards collapsed so the list doesn't flood the panel on load
        setCollapsedMembers(new Set(mapped.map((m) => m.user_id)));
        setAddRoleByUser({});
        setMemberPlatformRoleEdits({});
      }
      if (!memberLocsRpc.error) {
        const locs: Record<string, string[]> = {};
        for (const row of (memberLocsRpc.data ?? []) as { user_id: string; location_config_ids: string[] }[]) {
          locs[row.user_id] = [...new Set(row.location_config_ids)];
        }
        setMemberLocations(locs);
      }
      if (!memberPlantsRpc.error) {
        const plantsByUser: Record<string, string[]> = {};
        for (const row of (memberPlantsRpc.data ?? []) as { user_id: string; plant_ids: string[] }[]) {
          plantsByUser[row.user_id] = [...new Set((row.plant_ids ?? []).map(String))];
        }
        setMemberPlants(plantsByUser);
      } else {
        setMemberPlants({});
      }
      if (!orgPlantsRpc.error) {
        setOrgPlantOptions(
          ((orgPlantsRpc.data ?? []) as Array<{ id: string; plant_code: string; name: string }>).map(
            (p) => ({ id: p.id, plant_code: p.plant_code, name: p.name }),
          ),
        );
      } else {
        setOrgPlantOptions([]);
      }
      if (!platformRolesRpc.error) {
        const pr: Record<string, string> = {};
        for (const row of (platformRolesRpc.data ?? []) as { user_id: string; platform_role: string }[]) {
          pr[row.user_id] = row.platform_role;
        }
        setPlatformRoleByUser(pr);
      }

      const caps = await fetchOrgMemberCapabilityMap(effectiveOrgId);
      setMemberCapabilityByUser(caps);
    } catch (e: unknown) {
      setMsg(formatAdminError(e));
    } finally {
      setLoading(false);
    }
  }, [effectiveOrgId, sb, auth.memberOrganizationIds, auth, canRunAdminRpcs]);

  useAdminPanelOrgOptions(sb, auth, setOrgOptions);

  useEffect(() => {
    if (workspaceOrganizationId || !orgOptions[0]?.id) return;
    setWorkspaceOrganizationId(orgOptions[0].id);
  }, [workspaceOrganizationId, orgOptions, setWorkspaceOrganizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ── Action helpers ─────────────────────────────────────────────────────────
  function getRoleFor(userId: string): OrgRole {
    return roleByUser[userId] ?? "sales";
  }

  function handleRoleError(error: any, _role: OrgRole): never {
    throw error;
  }

  function toggleMember(uid: string) {
    setCollapsedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  }

  const {
    approveAndAssign, assignRole, block, addOrgRole, removeOrgRole,
    updateMemberPlatformRole, deactivateMembership, addUserLocation, removeUserLocation,
    addUserPlant, removeUserPlant,
    setMemberAdsRunner,
  } = useAdminPanelMemberActions({
    sb, effectiveOrgId, blockReasonByUser, memberPlatformRoleEdits, refresh,
    formatAdminError, getRoleFor, handleRoleError, setMsg, setLoading, setAddLocByUser,
    setAddPlantByUser,
  });

  const { deleteUser, resendInvite, submitInvite, mergeDuplicateUsers } = useAdminPanelAccountActions({
    auth, effectiveOrgId, inviteEmail, inviteRole, invitePlatformRole, mergeSourceUserId, mergeTargetUserId,
    refresh, formatAdminError, setMsg, setLoading, setInviteMsg, setInviteLoading, setInviteEmail,
    setInvitePlatformRole, setMergeBusy, setMergeSourceUserId, setMergeTargetUserId,
  });

  const embedded = !!props.embedded;
  const displayOrgName = orgOptions.find((o) => o.id === effectiveOrgId)?.name ?? activeOrgName;
  const modalTitle = activeModal === "users" ? "App User Admin" : activeModal === "contacts" ? "Contacts Directory" : activeModal === "org" ? "Org Administration" : "";
  const hasUserPending = pendingProfiles.length > 0 || approvedRolePending.length > 0;

  const onOrgChange = (nextIdRaw: string) => {
    const nextId = nextIdRaw || null;
    const nextOrg = orgOptions.find((o) => o.id === nextId);
    if (!nextId || !nextOrg) return;
    if (
      !window.confirm(
        `Switch workspace organization to "${nextOrg.name}"?\n\n` +
          `Admin, Knowledge Base (drafts and saves target this org unless you pick another scope), org branding, CRM tools, lead sync, workplace notifications, and Deals will use this org until you switch again.`
      )
    ) return;
    setWorkspaceOrganizationId(nextId);
  };

  return <AdminPanelContent {...{
    auth, embedded, activeOrgId, effectiveOrgId, displayOrgName, orgOptions, loading, msg, setMsg,
    refresh, hasUserPending, activeModal, setActiveModal, modalTitle, open, toggle, onOrgChange,
    pendingProfiles, approvedRolePending, rolesByUserId, getRoleFor, setRoleByUser, blockReasonByUser,
    setBlockReasonByUser, canPermanentlyDeleteUsers, canAssignManufacturingOrgRole, approveAndAssign, assignRole, block, deleteUser,
    activeMembers, currentUserId, collapsedMembers, toggleMember, platformRoleByUser, memberPlatformRoleEdits,
    setMemberPlatformRoleEdits, memberLocations, addRoleByUser, setAddRoleByUser, addLocByUser, setAddLocByUser,
    memberPlants, orgPlantOptions, addPlantByUser, setAddPlantByUser, addUserPlant, removeUserPlant,
    updateMemberPlatformRole, deactivateMembership, removeOrgRole, addOrgRole, addUserLocation, removeUserLocation,
    setMemberAdsRunner, memberCapabilityByUser,
    resendInvite, showMergeUsers, setShowMergeUsers, mergeSourceUserId, setMergeSourceUserId, mergeTargetUserId,
    setMergeTargetUserId, mergeBusy, mergeUserOptions, mergeDuplicateUsers, activeOrgName, inviteEmail,
    setInviteEmail, inviteRole, setInviteRole, invitePlatformRole, setInvitePlatformRole, inviteLoading,
    inviteMsg, submitInvite,
  }} />;
}
